import {mediaQuality, mediaTypes} from "@/domain/galleries/constants"
import metric_message from "@/domain/metrics/message"

const NATIVE_SHARE_PLATFORM = /iPhone|iPad/i
const DEFAULT_IDLE_AFTER_MS = 800

export const DOWNLOAD_STAGE = {
    PREPARING: 'preparing',
    DOWNLOADING: 'downloading',
}

export function resolveMediaDownloadUrl(media, quality = mediaQuality.AUTO) {
    if (!media) return null

    if (quality === mediaQuality.AUTO) {
        return media.download_hd_url || media.download_sd_url || null
    }

    if (quality === mediaQuality.SD) {
        if (media.media_type === mediaTypes.VIDEO) {
            return media.download_hd_url || media.video_preview_url || media.download_sd_url || null
        }
        return media.download_sd_url || null
    }

    if (quality === mediaQuality.HD) {
        if (!media.download_hd_url) return null
        return media.download_hd_url.includes('cdn.getpica.com')
            ? `${media.download_hd_url}/download`
            : media.download_hd_url
    }

    return null
}

export function canNativeShare() {
    return typeof navigator.share === 'function'
        && NATIVE_SHARE_PLATFORM.test(navigator.userAgent)
}

export function trackMediaDownload(media) {
    metric_message.photoDownloaded(media.id, media.media_type === mediaTypes.VIDEO)
}

/**
 * Triggers a single media download. On iPhone/iPad with `navigator.share`
 * available, opens the OS share sheet with the file; otherwise falls back
 * to a same-window navigation to the media URL (CDN serves it as an
 * attachment).
 *
 * @param {Object} media
 * @param {Object} [options]
 * @param {string} [options.quality=mediaQuality.AUTO]
 * @param {boolean} [options.trackMetric=true]
 * @param {number} [options.idleAfterMs=1000]   Delay after triggering the
 *   non-native download. Useful in loops, so the browser has time to start
 *   each download before the next href change.
 * @returns {Promise<boolean>} true when a download was initiated.
 */
export async function downloadMedia(media, options = {}) {
    const {
        quality = mediaQuality.AUTO,
        trackMetric = true,
        idleAfterMs = DEFAULT_IDLE_AFTER_MS,
    } = options

    const url = resolveMediaDownloadUrl(media, quality)
    if (!url) return false

    if (trackMetric) {
        trackMediaDownload(media)
    }

    if (canNativeShare()) {
        const file = await fetchMediaFile(media, url)
        if (file && await shareFiles([file])) return true
        triggerBrowserDownload(url)
        return true
    }

    triggerBrowserDownload(url)
    if (idleAfterMs > 0) {
        await wait(idleAfterMs)
    }
    return true
}

/**
 * Downloads multiple media in one operation.
 *
 * On iPhone/iPad: pre-fetches every file then opens a single OS share sheet
 * with all of them via `navigator.share({files})`. If the platform refuses
 * (canShare false) it falls back to a sequential `downloadMedia` loop.
 *
 * Otherwise: sequential `downloadMedia` calls (one browser download each,
 * spaced by `idleAfterMs`).
 *
 * Pass `options.signal` (AbortSignal) to cancel the run. When aborted, the
 * helper stops the loop and resolves; in-flight blob fetches (iOS path)
 * are cancelled too.
 *
 * @param {Array<Object>} medias
 * @param {Object} [options]
 * @param {string} [options.quality=mediaQuality.AUTO]
 * @param {boolean} [options.trackMetric=true]
 * @param {number} [options.idleAfterMs=1000]
 * @param {(progress: {stage: string, current: number, total: number}) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<number>} count of successful downloads/shares.
 */
export async function downloadMedias(medias, options = {}) {
    const {
        quality = mediaQuality.AUTO,
        trackMetric = true,
        idleAfterMs = DEFAULT_IDLE_AFTER_MS,
        onProgress,
        signal,
    } = options

    if (!medias || medias.length === 0) return 0
    if (isAborted(signal)) return 0

    if (canNativeShare()) {
        const shared = await nativeShareMedias(medias, {quality, trackMetric, onProgress, signal})
        if (shared !== null) return shared
        // canShare refused; fall through to sequential downloads
    }

    let count = 0
    for (let i = 0; i < medias.length; i++) {
        if (isAborted(signal)) break
        const ok = await downloadMedia(medias[i], {quality, trackMetric, idleAfterMs})
        if (ok) count += 1
        report(onProgress, DOWNLOAD_STAGE.DOWNLOADING, i + 1, medias.length)
    }
    return count
}

async function nativeShareMedias(medias, {quality, trackMetric, onProgress, signal}) {
    const files = []

    for (let i = 0; i < medias.length; i++) {
        if (isAborted(signal)) return 0
        const media = medias[i]
        const url = resolveMediaDownloadUrl(media, quality)
        if (!url) {
            report(onProgress, DOWNLOAD_STAGE.PREPARING, i + 1, medias.length)
            continue
        }
        const file = await fetchMediaFile(media, url, signal)
        if (!file) {
            // Fetch failed (CORS, network, etc.). Bail out and let the caller
            // fall back to the sequential browser-download path.
            if (isAborted(signal)) return 0
            return null
        }
        files.push(file)
        if (trackMetric) trackMediaDownload(media)
        report(onProgress, DOWNLOAD_STAGE.PREPARING, i + 1, medias.length)
    }

    if (isAborted(signal) || files.length === 0) return 0
    if (!(navigator.canShare && navigator.canShare({files}))) {
        // Platform does not support sharing this batch.
        return null
    }

    try {
        await navigator.share({files})
    } catch (error) {
        if (error.name === 'AbortError') return 0
        return null
    }
    return files.length
}

async function fetchMediaFile(media, url, signal) {
    try {
        const response = await fetch(url, {method: 'GET', mode: 'cors', signal})
        const blob = await response.blob()
        const isVideo = media.media_type === mediaTypes.VIDEO
        const extension = isVideo ? 'mp4' : 'jpg'
        const mimeType = isVideo ? 'video/mp4' : 'image/jpeg'
        return new File([blob], `media_${media.id}.${extension}`, {type: mimeType})
    } catch (error) {
        return null
    }
}

function isAborted(signal) {
    return signal != null && signal.aborted === true
}

async function shareFiles(files) {
    if (!(navigator.canShare && navigator.canShare({files}))) return false
    try {
        await navigator.share({files})
        return true
    } catch (error) {
        return error.name === 'AbortError'
    }
}

function triggerBrowserDownload(url) {
    location.href = url
}

function report(onProgress, stage, current, total) {
    if (typeof onProgress === 'function') {
        onProgress({stage, current, total})
    }
}

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}
